import React, { useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { REGIONS_COUNTRY_CODES } from '@luxuryescapes/lib-regions/lib/phoneNumberValidation';

import CustomerServiceRepSelector from '~/components/LuxLoyalty/CustomerServiceRepSelector';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { useEnrolLuxLoyaltyAccount } from '~/hooks/apis/loyalty/useEnrolLuxLoyaltyAccount';

import { getInfiniteOnboardingEmailWarning } from '~/services/LuxLoyaltyService';
import UsersService from '~/services/UsersService';

interface FormState {
  givenName: string;
  surname: string;
  email: string;
  phonePrefix: string;
  phone: string;
}

const DEFAULT_PHONE_PREFIX = '61';

/**
 * Mirrors the phone-prefix dropdown used by the user-detail Update User form.
 * Preferred regions first, then everything else alphabetised, with US/CA
 * merged into a single entry after position 2.
 */
const PHONE_PREFIX_OPTIONS: ReadonlyArray<{ value: string; label: string }> = (() => {
  const preferred = ['AU', 'NZ', 'GB', 'SG'];
  const merged = ['US', 'CA'];
  const sortedRegionCodes = [
    ...preferred,
    ...Object.keys(REGIONS_COUNTRY_CODES)
      .filter((code) => !preferred.includes(code) && !merged.includes(code))
      .sort((a, b) => REGIONS_COUNTRY_CODES[a].countryName.localeCompare(REGIONS_COUNTRY_CODES[b].countryName)),
  ];

  const items = sortedRegionCodes.map((regionCode) => {
    const region = REGIONS_COUNTRY_CODES[regionCode];
    return {
      value: region.countryCode,
      label: `${region.countryName} - ${regionCode} (+${region.countryCode})`,
    };
  });

  items.splice(2, 0, { value: '1', label: 'United States / Canada - US/CA (+1)' });
  return items;
})();
const EMPTY_STATE: FormState = {
  givenName: '',
  surname: '',
  email: '',
  phonePrefix: DEFAULT_PHONE_PREFIX,
  phone: '',
};

/** Loose RFC-style check; final validation happens server-side. */
function isLikelyEmail(value: string): boolean {
  return /.+@.+\..+/.test(value);
}

/**
 * Generate a strong random password client-side. The password is never shown
 * to the admin; the customer will reset it via the standard forgot-password
 * flow.
 */
function generateRandomPassword(): string {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  // Base64 url-safe, no padding — gives ~32 chars of high-entropy ASCII.
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!$%&*?';
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += alphabet[bytes[i] % alphabet.length];
  }
  return out;
}

/**
 * Create-account tab: registers a new LE-brand customer and immediately
 * enrols them at the Infinite Société tier with a dedicated rep. Both steps
 * run in sequence; the rep is mandatory.
 */
type Outcome =
  | { kind: 'success'; customerName: string; customerId: string }
  | { kind: 'email-warning'; customerName: string; customerId: string; message: string }
  | { kind: 'partial'; customerEmail: string; customerId: string; message: string }
  | { kind: 'error'; message: string };

export default function CreateInfiniteAccountTab() {
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [csRepId, setCsRepId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const trimmed: FormState = {
    givenName: form.givenName.trim(),
    surname: form.surname.trim(),
    email: form.email.trim(),
    phonePrefix: form.phonePrefix.trim(),
    phone: form.phone.trim(),
  };

  const errors = {
    givenName: !trimmed.givenName ? 'First name is required.' : null,
    surname: !trimmed.surname ? 'Last name is required.' : null,
    email: !trimmed.email
      ? 'Email is required.'
      : !isLikelyEmail(trimmed.email)
        ? 'Enter a valid email address.'
        : null,
    phonePrefix: !trimmed.phonePrefix ? 'Country code is required.' : null,
    phone: !trimmed.phone ? 'Phone is required.' : null,
    csRep: csRepId ? null : 'Select a rep to take this customer.',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const enrolMutation = useEnrolLuxLoyaltyAccount();

  const resetForm = () => {
    setForm(EMPTY_STATE);
    setCsRepId(null);
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (hasErrors || !csRepId) {
      return;
    }
    setIsSubmitting(true);
    setOutcome(null);

    let createdCustomerId: string;

    // Step 1: register the LE account.
    try {
      // Admin-gated flags from svc-auth#1875: this admin-driven Infinite flow
      // pre-verifies the email and skips the customer-facing confirmation email
      // since the customer never sees the auto-generated password.
      const payload = {
        givenName: trimmed.givenName,
        surname: trimmed.surname,
        email: trimmed.email,
        phone_prefix: trimmed.phonePrefix,
        phone: trimmed.phone,
        password: generateRandomPassword(),
        brand: LUXURY_ESCAPES,
        email_verified: true,
        bypass_confirmation_email: true,
      };
      const created = await UsersService.registerUser(payload);
      const id = (created?.id_member as string | undefined) ?? (created?.memberId as string | undefined);
      if (!id) {
        throw new Error('Account registered but no customer id was returned by the auth service.');
      }
      createdCustomerId = id;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error.';
      setOutcome({ kind: 'error', message: `Failed to register customer: ${message}` });
      setIsSubmitting(false);
      return;
    }

    // Step 2: enrol the new account at the infinite tier with the chosen rep.
    const customerName = `${trimmed.givenName} ${trimmed.surname}`;
    const customerEmail = trimmed.email;
    try {
      const enrolResponse = await enrolMutation.mutateAsync({
        customerId: createdCustomerId,
        tier: 'infinite',
        customerServiceRepId: csRepId,
      });
      const onboardingEmailWarning = getInfiniteOnboardingEmailWarning(enrolResponse);
      if (onboardingEmailWarning) {
        setOutcome({
          kind: 'email-warning',
          customerName,
          customerId: createdCustomerId,
          message: onboardingEmailWarning,
        });
        resetForm();
        return;
      }
      setOutcome({ kind: 'success', customerName, customerId: createdCustomerId });
      resetForm();
    } catch (error) {
      // The Société enrol endpoint is atomic: a failure means nothing was
      // written, including the rep mapping. No retry is needed.
      const message = error instanceof Error ? error.message : 'Unknown error.';
      setOutcome({ kind: 'partial', customerEmail, customerId: createdCustomerId, message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: { xs: 3, md: 4 },
        maxWidth: 760,
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        borderTop: 4,
        borderTopColor: 'transparent',
        backgroundImage: `linear-gradient(white, white), ${theme.palette.loyaltyTiers.infinite.gradient}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      })}
    >
      <Stack spacing={1} mb={3}>
        <Typography variant="overline" sx={{ letterSpacing: 1.5, color: 'text.secondary' }}>
          Société · Infinite
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          Create Infinite account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Registers a new Luxury Escapes customer and enrols them at the Infinite Société tier with a dedicated Customer
          Service Rep. The customer&apos;s password is generated automatically; they&apos;ll reset it via the standard
          forgot-password flow.
        </Typography>
      </Stack>

      {outcome && (
        <Alert
          severity={outcome.kind === 'success' ? 'success' : outcome.kind === 'error' ? 'error' : 'warning'}
          onClose={() => setOutcome(null)}
          sx={{ borderRadius: 2, mb: 3 }}
        >
          {outcome.kind === 'success' && (
            <>
              <AlertTitle>Account created and enrolled</AlertTitle>
              <Typography variant="body2">
                <strong>{outcome.customerName}</strong> is now an Infinite member.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Customer id:{' '}
                <MuiLink
                  component={Link}
                  to={`/users/${outcome.customerId}`}
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {outcome.customerId}
                </MuiLink>
              </Typography>
            </>
          )}
          {outcome.kind === 'email-warning' && (
            <>
              <AlertTitle>Account created and enrolled</AlertTitle>
              <Typography variant="body2">
                <strong>{outcome.customerName}</strong> is now an Infinite member, but the onboarding email needs
                attention.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Customer id:{' '}
                <MuiLink
                  component={Link}
                  to={`/users/${outcome.customerId}`}
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {outcome.customerId}
                </MuiLink>{' '}
                · {outcome.message}
              </Typography>
            </>
          )}
          {outcome.kind === 'partial' && (
            <>
              <AlertTitle>Account created — enrolment failed</AlertTitle>
              <Typography variant="body2">
                Customer <strong>{outcome.customerEmail}</strong> was created, but the Société enrolment step failed.
                Finish enrolment manually from the user detail page.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Customer id:{' '}
                <MuiLink
                  component={Link}
                  to={`/users/${outcome.customerId}`}
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {outcome.customerId}
                </MuiLink>{' '}
                · Reason: {outcome.message}
              </Typography>
            </>
          )}
          {outcome.kind === 'error' && (
            <>
              <AlertTitle>Could not create account</AlertTitle>
              <Typography variant="body2">{outcome.message}</Typography>
            </>
          )}
        </Alert>
      )}

      <Stack spacing={3}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8 }}
          >
            Customer details
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={form.givenName}
                onChange={(e) => setForm({ ...form, givenName: e.target.value })}
                required
                fullWidth
                error={submitted && !!errors.givenName}
                helperText={submitted ? (errors.givenName ?? ' ') : ' '}
                disabled={isSubmitting}
              />
              <TextField
                label="Last name"
                value={form.surname}
                onChange={(e) => setForm({ ...form, surname: e.target.value })}
                required
                fullWidth
                error={submitted && !!errors.surname}
                helperText={submitted ? (errors.surname ?? ' ') : ' '}
                disabled={isSubmitting}
              />
            </Stack>

            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              fullWidth
              type="email"
              error={submitted && !!errors.email}
              helperText={submitted ? (errors.email ?? ' ') : ' '}
              disabled={isSubmitting}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Country code"
                value={form.phonePrefix}
                onChange={(e) => setForm({ ...form, phonePrefix: e.target.value })}
                required
                sx={{ minWidth: 260 }}
                error={submitted && !!errors.phonePrefix}
                helperText={submitted ? (errors.phonePrefix ?? ' ') : ' '}
                disabled={isSubmitting}
              >
                {PHONE_PREFIX_OPTIONS.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
                fullWidth
                error={submitted && !!errors.phone}
                helperText={submitted ? (errors.phone ?? ' ') : ' '}
                disabled={isSubmitting}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8 }}
          >
            Membership
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 999,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <StorefrontOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Brand · <strong>Luxury Escapes</strong>
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={(theme) => ({
                px: 1.5,
                py: 1,
                borderRadius: 999,
                color: theme.palette.loyaltyTiers.infinite.contrastText,
                background: theme.palette.loyaltyTiers.infinite.gradient,
                '& .MuiSvgIcon-root': { color: 'inherit' },
              })}
            >
              <AllInclusiveIcon fontSize="small" />
              <Typography variant="body2" fontWeight={700} color="inherit">
                Target tier · Infinite
              </Typography>
            </Stack>
          </Stack>

          <CustomerServiceRepSelector
            value={csRepId}
            onChange={setCsRepId}
            required
            error={submitted && !csRepId}
            helperText={
              submitted && !csRepId ? errors.csRep : 'This rep will be linked to the new account as part of enrolment.'
            }
            disabled={isSubmitting}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" pt={1}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={(theme) => ({
              px: 3.5,
              py: 1.1,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              color: 'common.white',
              background: theme.palette.loyaltyTiers.infinite.gradient,
              '&:hover': {
                background: theme.palette.loyaltyTiers.infinite.gradient,
                filter: 'brightness(0.95)',
              },
            })}
          >
            {isSubmitting ? 'Creating…' : 'Create account & enrol'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
